'use client';

import {
  useState,
  useLayoutEffect,
  useContext,
  createContext,
  RefCallback,
} from 'react';

interface ViewportSize {
  width: number;
  height: number;
}

const ContainerSizeContext = createContext<ViewportSize | null>(null);

export function useContainerSize() {
  const containerSize = useContext(ContainerSizeContext);

  if (containerSize === null) {
    throw new Error(
      'useContainerSize has to be used within <ContainerSizeProvider>',
    );
  }

  return containerSize;
}

interface Props {
  children: (ref: RefCallback<HTMLElement>) => React.ReactNode;
}

export default function ContainerSizeProvider({ children }: Props) {
  const [container, setContainer] = useState<HTMLElement | null>();
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    function updateContanerSize() {
      if (container != null) {
        const { offsetWidth: width, offsetHeight: height } = container;
        setContainerSize({ width, height });
      }
    }

    updateContanerSize();

    window?.addEventListener('resize', updateContanerSize);
    container?.addEventListener('resize', updateContanerSize);

    return () => {
      window?.removeEventListener('resize', updateContanerSize);
      container?.removeEventListener('resize', updateContanerSize);
    };
  }, [container]);

  return (
    <ContainerSizeContext.Provider value={containerSize}>
      {children(setContainer)}
    </ContainerSizeContext.Provider>
  );
}
